/**
 * ADR-0210 Slice 4 — "Worth knowing" strip at the top of the Agents view.
 *
 * Insights arrive one per (kind, day, subject). People read stories, not
 * rows: a new agent is one sentence that says who it is ("Claude-User
 * started visiting — Anthropic's Claude fetching pages a person asked
 * for"), the same name never appears twice, and once there are many new
 * names on a day they fold into one line. Every other kind is a short
 * sentence. At most five lines until expanded. Derived data, never
 * signed; payloads are numbers, display names and the classifier's
 * one-line description, rendered as text.
 */

import { useState } from 'react';
import { AlertTriangle, Sparkles, TrendingUp, UserPlus, X, ShieldOff } from 'lucide-react';

import type { AgentInsight } from '@sill/types/api';

import { useC } from '../../lib/theme.js';
import { fmtDay, fmtInt, monoFont, sansFont } from './format.js';

const MAX_COLLAPSED = 5;
const MAX_NAMES = 4;

function num(p: Readonly<Record<string, unknown>>, k: string): number {
  return Number(p[k] ?? 0);
}
function str(p: Readonly<Record<string, unknown>>, k: string): string | null {
  const v = p[k];
  return typeof v === 'string' && v.length > 0 ? v : null;
}

/** The classifier's one-line "who" as a clause (no trailing period). */
function whoClause(p: Readonly<Record<string, unknown>>): string | null {
  const w = str(p, 'who');
  if (w === null) return null;
  const t = w.replace(/\s+/g, ' ').trim().replace(/[.\s]+$/, '').slice(0, 120);
  return t.length > 0 ? t : null;
}

/** Short sentence for a single non-grouped insight. */
export function insightText(i: AgentInsight): string {
  const p = i.payload;
  switch (i.kind) {
    case 'new_agent_on_site': {
      const who = whoClause(p);
      const traffic = num(p, 'traffic');
      return `${str(p, 'name') ?? 'An AI agent'} started visiting${who !== null ? ` — ${who}` : ''}${traffic > 0 ? ` (${fmtInt(traffic)} page loads so far)` : ''}`;
    }
    case 'new_agent_on_network': {
      const who = whoClause(p);
      return `${str(p, 'name') ?? 'An AI agent'} is new to the Sill network this week and already visiting you${who !== null ? ` — ${who}` : ''}`;
    }
    case 'traffic_spike':
      return `Traffic spike: ${fmtInt(num(p, 'today'))} page loads, ${p.factor ?? '?'}× a typical day${str(p, 'top_agent') ? ` — mostly ${str(p, 'top_agent')}` : ''}`;
    case 'refusal_rate_change':
      return `Refusals up: ${Math.round(num(p, 'today_rate') * 100)}% of agent requests refused (was ${Math.round(num(p, 'prior_rate_7d') * 100)}%)${str(p, 'top_reason') ? ` — mostly ${str(p, 'top_reason')!.replace(/_/g, ' ')}` : ''}`;
    case 'demand_unavailable':
      return `Wanted but unavailable: ${str(p, 'title') ?? 'a product'} (×${fmtInt(num(p, 'requests'))})`;
    default:
      return i.kind.replace(/_/g, ' ');
  }
}

interface Line {
  readonly key: string;
  readonly kind: string;
  readonly day: string;
  readonly text: string;
  /** Clickable agent names inside the line (grouped "new agent" lines). */
  readonly names: ReadonlyArray<{ name: string; agent_key: string | null }>;
  readonly insight_ids: readonly string[];
}

/** Only fold "new agent" lines once a day has more than this many. */
const GROUP_AFTER = 3;

/** De-duplicate new-agent insights by name (a second UA build of the same
 *  product is the same visitor to a person), keep them as sentences while
 *  there are few, fold them into one line per (kind, day) once there are
 *  many. Everything else stays one per line. */
export function groupInsights(insights: readonly AgentInsight[]): Line[] {
  const lines: Line[] = [];
  const groups = new Map<string, AgentInsight[]>();
  // name → the insight shown for it; later same-name insights hang their
  // ids off it so a dismiss clears the duplicates too.
  const seen = new Map<string, AgentInsight>();
  const extra_ids = new Map<string, string[]>();
  for (const i of insights) {
    if (i.kind === 'new_agent_on_site' || i.kind === 'new_agent_on_network') {
      const name_key = (str(i.payload, 'name') ?? 'an AI agent').toLowerCase();
      const first = seen.get(name_key);
      if (first !== undefined) {
        extra_ids.set(first.insight_id, [...(extra_ids.get(first.insight_id) ?? []), i.insight_id]);
        continue;
      }
      seen.set(name_key, i);
      const k = `${i.kind}|${i.day}`;
      const g = groups.get(k) ?? [];
      g.push(i);
      groups.set(k, g);
    } else {
      lines.push({ key: i.insight_id, kind: i.kind, day: i.day, text: insightText(i), names: [], insight_ids: [i.insight_id] });
    }
  }
  for (const [k, g] of groups) {
    const kind = g[0]!.kind;
    if (g.length <= GROUP_AFTER) {
      for (const i of g) {
        lines.push({ key: i.insight_id, kind, day: i.day, text: insightText(i), names: [{ name: str(i.payload, 'name') ?? 'an AI agent', agent_key: i.agent_key }], insight_ids: [i.insight_id, ...(extra_ids.get(i.insight_id) ?? [])] });
      }
      continue;
    }
    const names = g.map((i) => ({ name: str(i.payload, 'name') ?? 'an AI agent', agent_key: i.agent_key }));
    const shown = names.slice(0, MAX_NAMES).map((n) => n.name).join(', ');
    const more = names.length > MAX_NAMES ? ` (+${names.length - MAX_NAMES})` : '';
    const prefix = kind === 'new_agent_on_site' ? `${g.length} new visitors: ` : `${g.length} new to the Sill network and already visiting you: `;
    lines.push({ key: k, kind, day: g[0]!.day, text: `${prefix}${shown}${more}`, names, insight_ids: g.flatMap((i) => [i.insight_id, ...(extra_ids.get(i.insight_id) ?? [])]) });
  }
  lines.sort((a, b) => (a.day < b.day ? 1 : a.day > b.day ? -1 : 0));
  return lines;
}

function iconFor(kind: string): { Icon: typeof Sparkles; tone: 'lime' | 'amber' | 'coral' | 'sky' } {
  switch (kind) {
    case 'new_agent_on_site': return { Icon: UserPlus, tone: 'sky' };
    case 'new_agent_on_network': return { Icon: Sparkles, tone: 'sky' };
    case 'traffic_spike': return { Icon: TrendingUp, tone: 'lime' };
    case 'refusal_rate_change': return { Icon: ShieldOff, tone: 'coral' };
    case 'demand_unavailable': return { Icon: AlertTriangle, tone: 'amber' };
    default: return { Icon: Sparkles, tone: 'sky' };
  }
}

export interface InsightsStripProps {
  readonly insights: readonly AgentInsight[];
  readonly onDismiss: (insight_ids: readonly string[]) => void;
  readonly onOpenAgent?: ((agent_key: string) => void) | undefined;
  /**
   * Dismissal is owner/admin only server-side. The dashboard hides
   * the "Dismiss all" + per-insight X affordances for reviewer /
   * viewer (defense-in-depth; the API 403s regardless).
   */
  readonly canDismiss?: boolean;
}

export function InsightsStrip({ insights, onDismiss, onOpenAgent, canDismiss = true }: InsightsStripProps): JSX.Element | null {
  const C = useC();
  const [expanded, setExpanded] = useState(false);
  if (insights.length === 0) return null;
  const lines = groupInsights(insights);
  const visible = expanded ? lines : lines.slice(0, MAX_COLLAPSED);
  const hidden = lines.length - visible.length;
  const toneColor = { lime: C.limeFg, amber: C.amberFg, coral: C.coralFg, sky: C.skyFg } as const;

  return (
    <div data-testid="insights-strip" style={{ border: `1px solid ${C.border}`, borderRadius: 2, background: C.surface, padding: '12px 14px', marginBottom: 16 }}>
      <div className="flex items-center justify-between" style={{ marginBottom: 8 }}>
        <div style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: C.muted }}>
          Worth knowing · last 7 days
        </div>
        {canDismiss && lines.length > 1 ? (
          <button
            type="button"
            onClick={() => onDismiss(lines.flatMap((l) => l.insight_ids))}
            style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', fontFamily: monoFont, fontSize: 9, letterSpacing: 1.5, color: C.dim, textTransform: 'uppercase' }}
          >
            Dismiss all
          </button>
        ) : null}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {visible.map((l) => {
          const { Icon, tone } = iconFor(l.kind);
          const single = l.names.length === 1 && l.names[0]!.agent_key !== null && onOpenAgent !== undefined;
          return (
            <div key={l.key} data-insight-line={l.kind} style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 10, alignItems: 'start' }}>
              <Icon size={13} color={toneColor[tone]} style={{ marginTop: 3 }} />
              <div style={{ fontFamily: sansFont, fontSize: 13, color: C.text, lineHeight: 1.45 }}>
                {single ? (
                  <button
                    type="button"
                    onClick={() => onOpenAgent(l.names[0]!.agent_key!)}
                    style={{ background: 'transparent', border: 'none', padding: 0, textAlign: 'left', font: 'inherit', color: 'inherit', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3, textDecorationColor: C.dim }}
                  >
                    {l.text}
                  </button>
                ) : l.names.length > 1 && onOpenAgent !== undefined ? (
                  <span>
                    {l.text.slice(0, l.text.indexOf(':') + 2)}
                    {l.names.slice(0, MAX_NAMES).map((n, idx) => (
                      <span key={`${n.name}-${idx}`}>
                        {idx > 0 ? ', ' : ''}
                        {n.agent_key !== null ? (
                          <button
                            type="button"
                            onClick={() => onOpenAgent(n.agent_key!)}
                            style={{ background: 'transparent', border: 'none', padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3, textDecorationColor: C.dim }}
                          >
                            {n.name}
                          </button>
                        ) : n.name}
                      </span>
                    ))}
                    {l.names.length > MAX_NAMES ? ` (+${l.names.length - MAX_NAMES})` : ''}
                  </span>
                ) : (
                  <span>{l.text}</span>
                )}
                <span style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 1, color: C.dim, marginLeft: 8 }}>{fmtDay(l.day)}</span>
              </div>
              {canDismiss ? (
                <button
                  type="button"
                  aria-label="Dismiss insight"
                  onClick={() => onDismiss(l.insight_ids)}
                  style={{ background: 'transparent', border: 'none', padding: 2, cursor: 'pointer', color: C.muted, display: 'inline-flex' }}
                >
                  <X size={12} />
                </button>
              ) : (
                <span />
              )}
            </div>
          );
        })}
      </div>
      {hidden > 0 || expanded ? (
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          style={{ marginTop: 8, background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', fontFamily: monoFont, fontSize: 9, letterSpacing: 1.5, color: C.muted, textTransform: 'uppercase' }}
        >
          {expanded ? 'Show fewer' : `Show ${hidden} more`}
        </button>
      ) : null}
    </div>
  );
}
